import { useState, useEffect, useRef } from "react";
import { displayLogoURL, isValidImage, resizeImageToBase64 } from "../utilities/functions";

export default function ImageInputModal({ type, logofilename, logofile, logourl, onSave, onClose }) {
    const [showAlert, setShowAlert] = useState(false);
    const [alertText, setAlertText] = useState("");
    const [image, setImage] = useState("");
    const [filename, setFilename] = useState("");
    const [urlInput, setUrlInput] = useState("");
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (type === "upload" && logofile) {
            setImage(logofile);
            setFilename(logofilename || "");
        } else if (type === "url" && logourl) {
            setImage(logourl);
            setUrlInput(logourl);
        }
    }, [type, logofile, logourl, logofilename]);

    const onFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        resizeImageToBase64(file)
            .then((data) => {
                if (data.valid) {
                    setImage(data.image);
                    setFilename(file.name);
                    setAlertText(data.resized
                        ? "Your image was resized to fit our display requirements. The quality has been preserved."
                        : ""
                    );
                    setShowAlert(data.resized);
                } else {
                    setImage("");
                    setFilename("");
                    setAlertText(`Please upload a logo smaller than ${process.env.REACT_APP_MAX_FILE_SIZE} MB.`);
                    setShowAlert(true);
                }
            })
            .catch(console.error);
    };

    const onURLAdded = (e) => {
        const value = e.target.value.trim();
        setUrlInput(value);
        if (!value) return;

        isValidImage(value).then((isValid) => {
            if (isValid) {
                setImage(value);
                setShowAlert(false);
            } else {
                setAlertText("The URL is not a valid image. Please enter a valid image link.");
                setShowAlert(true);
            }
        });
    };

    return (
        <div className="fixed inset-0 mt-0 z-50 grid place-items-center overflow-auto bg-black bg-opacity-50">
            <div className="w-11/12 max-w-[500px] rounded-lg border border-gray-700 bg-gray-800 flex flex-col gap-2 relative">
                <div className="py-3 px-4">
                    <button
                        className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl"
                        onClick={onClose}
                    >
                        ×
                    </button>

                    <h1 className="form-label mb-2">Logo Image</h1>

                    {showAlert && (
                        <div className="bg-blue-100 border border-blue-500 text-blue-700 px-3 py-2 mb-3 relative text-sm rounded">
                            <button
                                className="absolute top-1 right-2 text-lg text-gray-700"
                                onClick={() => setShowAlert(false)}
                            >
                                ×
                            </button>
                            {alertText}
                        </div>
                    )}

                    <div className="w-full py-2">
                        {image && (
                            <div className="mb-3">{displayLogoURL(image)}</div>
                        )}

                        {type === "upload" ? (
                            <>
                                <input
                                    ref={fileInputRef}
                                    className="form-input w-full text-sm rounded-lg border border-gray-600 bg-gray-700 text-white"
                                    type="file"
                                    accept="image/*"
                                    onChange={onFileUpload}
                                />
                                {
                                    <p className="text-xs text-gray-400 mt-1">Previously uploaded: {logofilename}</p>
                                }
                            </>
                        ) : (
                            <input
                                className="form-input w-full text-sm rounded-lg border border-gray-600 bg-gray-700 text-white"
                                type="text"
                                value={urlInput}
                                onChange={onURLAdded}
                                placeholder="Paste image URL"
                            />
                        )}
                    </div>

                    <p className="text-xs text-gray-300">
                        Saving will replace your existing business logo with the image above.
                    </p>

                    <div className="flex py-3 gap-4">
                        <button
                            onClick={() => onSave(type, image, type === "upload" ? filename : null)}
                            disabled={!image}
                            className="py-2 px-4 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            Save
                        </button>

                        <button
                            onClick={onClose}
                            className="py-2 px-4 text-sm font-medium text-gray-300 border border-gray-500 rounded hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
